import type { UseQueryResult } from "@tanstack/react-query";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import type { GetEventPublicViewOptions } from "@frontend/shared/features/events/domain/types/event.types";
import type { EventPublicView } from "@shared/schemas/event.schemas";
import type { EventJoinInfo } from "@shared/schemas/event-join-info.schemas";

export interface AnonymousActions {
  readonly useGetEventPublicView: (
    options: GetEventPublicViewOptions,
  ) => UseQueryResult<EventPublicView>;
  readonly useGetEventJoinInfo: (
    eventIdPublicOrSlug: string,
    options?: Parameters<FrontendDAL["events"]["useGetEventJoinInfo"]>[1],
  ) => UseQueryResult<EventJoinInfo>;
  readonly useLogin: () => ReturnType<FrontendDAL["auth"]["useLogin"]>;
  readonly useCreateSession: () => ReturnType<
    FrontendDAL["auth"]["useCreateSession"]
  >;
  readonly useCompleteOAuthSession: () => ReturnType<
    FrontendDAL["auth"]["useCompleteOAuthSession"]
  >;
  readonly useProcessMagicLink: () => ReturnType<
    FrontendDAL["magicLinks"]["useProcessMagicLink"]
  >;
}

/**
 * Anonymous (public) actions — mirrors the backend `AnonymousActions`. No auth
 * is required for the public surface, so this hook never returns null. It groups
 * the public reads (event public view + join message) and the auth-flow
 * operations (login, OTP session, OAuth callback, magic-link) as an actor-scoped
 * bundle; consumers invoke the returned sub-hooks at their component's top level
 * and React Query dedupes shared reads.
 */
export function useAnonymousActions(): AnonymousActions {
  return {
    useGetEventPublicView: (options) =>
      dal().events.useGetEventPublicView(options),
    useGetEventJoinInfo: (eventIdPublicOrSlug, options) =>
      dal().events.useGetEventJoinInfo(eventIdPublicOrSlug, options),
    useLogin: () => dal().auth.useLogin(),
    useCreateSession: () => dal().auth.useCreateSession(),
    useCompleteOAuthSession: () => dal().auth.useCompleteOAuthSession(),
    useProcessMagicLink: () => dal().magicLinks.useProcessMagicLink(),
  };
}
